import { BbState } from 'lib_ui-primitives';
import { constants, globalConfig as getGlobalConfig } from 'lib_ui-services';

/**
 * @typedef {import('@sstdev/types').STRATEGY_TYPES} STRATEGY_TYPES
 */

const { retention } = constants;
const { SCOPE, DURATION, VISIBILITY } = retention;

export const _private = { BbState, getGlobalConfig };

export default {
    verb: 'didUpdate',
    namespace: 'application',
    relation: 'useCase',
    type: 'syncAllDataToLocal',
    description: "Load the user's role so data right permissions can use it.",
    prerequisites: [
        {
            context: {
                verb: 'get',
                namespace: 'identity',
                relation: 'role',
                type: 'find'
            },
            query: ({ context }) => {
                const _id = context?.session?.role?._id;
                return { _id };
            }
        },
        {
            context: {
                verb: 'get',
                namespace: 'identity',
                relation: 'tenantSetting',
                type: 'find'
            }
        }
    ],
    //this is the actual logic:
    logic: onSyncFinished
};

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").Context} Context
 *
 * @typedef {import('@sstdev/types').TenantSettings} TenantSettings
 */

/**
 * @param {{
 *   data: any;
 *   prerequisiteResults: Array<{result:Array<object>}>;
 *   context: Context;
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {Promise}
 */
async function onSyncFinished({ prerequisiteResults = [], context, log }) {
    const [{ result: [dbRole = { _isDefault: true }] } = {}, { result: [tenantSetting = {}] } = {}] =
        prerequisiteResults;

    await setRoleInBBState(dbRole, context, log);

    await loadTenantSettingToBBState(tenantSetting);
}

async function setRoleInBBState(dbRole, context, log) {
    if (dbRole._isDefault) {
        // Note, if no role was found for the given _id,
        // or if we never get here due to an error thrown before here,
        // Then the dataRights filtering will never allow any data to be shown....
        log.error(`Failed to find user's role: ${dbRole.title}. Data filtering unavailable.`);
    }

    const {
        session: { role }
    } = context;

    const role_retention = {
        // DURATION.PAGE_LOAD/VISIBILITY.PER_BROWSER: this value gets cleared on page reload or login/out
        // but that is OK, as the sync runs in all of those scenarios.
        duration: DURATION.PAGE_LOAD,
        visibility: VISIBILITY.PER_BROWSER,
        scope: SCOPE.LOCAL,
        id: role._id
    };

    //useful for testing, hardcode as if there data rights
    // dbRole.system = false;
    // dbRole.dataRights = {
    //     'location:building': [{ title: 'Building 2', _id: '61a64035dce16fc2924358a5' }]
    // };
    const bbState = _private.BbState.connect(role_retention);
    await bbState.setAsync(dbRole, 'role');
}

/**
 *
 * @param {object} tenantSetting
 * @param {string} tenantSetting._id tenantId
 * @param {string} tenantSetting.title tenant name
 * @param {boolean} [tenantSetting.CUSTOM_FIELDS] Whether custom fields are enabled for this tenant
 * @param {boolean} [tenantSetting.clientDataRights] Whether clientDataRights are enabled for this tenant
 * @param {boolean} [tenantSetting.RFID_PRINTING] Whether RFID printing is enabled for this AT tenant
 * @param {object} [tenantSetting.rfid] rfid specific settings, see also lib_ui-services\src\rfidStrategy.js
 * @param {STRATEGY_TYPES} [tenantSetting.rfid.shortStrategy] Short AssetNo to RFID Tag Value Encoding Strategy
 * @param {STRATEGY_TYPES} [tenantSetting.rfid.longStrategy] Long AssetNo to RFID Tag Value Encoding Strategy
 * @param {number} [tenantSetting.rfid.maxShortLength] Max. assetNo Length for Short Value Encoding Strategy
 * @param {STRATEGY_TYPES} [tenantSetting.rfid.displayStrategy] How the hex tagId is displayed in the UI
 *
 */
async function loadTenantSettingToBBState(tenantSetting) {
    await _private.BbState.loadTenantSettings(tenantSetting);
    // Some places need access to RFID and Tenant Settings synchronously
    // e.g. when determining the appropriate RFID tag normalization policy in lib_ui-services/src/rfidStrategy.js
    const globalConfig = _private.getGlobalConfig();
    globalConfig.setRfidConfig(tenantSetting.rfid || {});
    globalConfig.setTenantSettings(tenantSetting);
}
